import * as React from 'react'
import { useLocation } from 'react-router-dom'
import {
  markFirstRender,
  markNavigationStart,
  trackPageView
} from '@axilon-apps/domains-ontology-ui-state'

/**
 * Per-screen TTID/TTFD instrumentation (AXI-3875) + product usage page_view.
 *
 * Mounted once inside the router (a sibling of the route `<Outlet />`), so it
 * observes every navigation — main app, operator, and standalone routes alike.
 * It owns only the React-specific timing / usage signals; measurement state
 * lives in the framework-agnostic trackers in the state package.
 *
 *  • Navigation start is captured *during render* (guarded by a ref) the moment
 *    the pathname OR query string changes, so query-param-driven screens
 *    (`/configure?type=rca` → `?type=pm`, `/plant/processes?process=…`, …) each
 *    open their own TTID/TTFD window. The metric label stays low-cardinality —
 *    markNavigationStart normalizes the URL (see route-label.ts).
 *  • The same location change also emits a product-usage `page_view` (gated by
 *    USAGE_ANALYTICS_ENABLED; best-effort, never blocks UX).
 *  • TTID (first paint of the route) is marked from a double-rAF scheduled
 *    after commit: the first frame runs after layout, the second after the
 *    browser has painted it.
 *
 * Renders nothing.
 */
export function RouteTelemetry(): null {
  const { pathname, search } = useLocation()
  const location = `${pathname}${search}`
  const lastLocationRef = React.useRef<string | null>(null)

  if (lastLocationRef.current !== location) {
    lastLocationRef.current = location
    markNavigationStart(location)
    trackPageView(location)
  }

  React.useEffect(() => {
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => markFirstRender())
    })
    return () => {
      cancelAnimationFrame(outer)
      if (inner) cancelAnimationFrame(inner)
    }
  }, [location])

  return null
}

export default RouteTelemetry
